import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Plus, Building2, MapPin, Phone, Mail, Globe, Map, List, Loader2,
  Briefcase, Home, UtensilsCrossed, Heart, GraduationCap, Church,
  Palette, DollarSign, Scale, Building, Baby, Monitor, Car, Scissors,
  MoreHorizontal
} from "lucide-react";
import { 
  getBusinessCategories, getBusinessListings, getMyBusinessListings,
  createBusinessListing, getBusinessMapListings
} from "@/lib/api";
import { toast } from "sonner";
import MediaUploader from "@/components/MediaUploader";
import RichTextEditor, { isContentEmpty } from "@/components/RichTextEditor";
import { DirectoryFilters, DirectoryMap, DirectoryList } from "@/components/directory";
import { MembershipBanner } from "@/components/MembershipModal";
import { useSEO } from "@/hooks/useSEO";

// Category icons mapping
const CATEGORY_ICONS = {
  "Professional Services": Briefcase,
  "Home Services": Home,
  "Food & Dining": UtensilsCrossed,
  "Health & Wellness": Heart,
  "Education": GraduationCap,
  "Religious Goods": Church,
  "Arts & Crafts": Palette,
  "Financial Services": DollarSign,
  "Legal Services": Scale,
  "Real Estate": Building,
  "Childcare": Baby,
  "Technology": Monitor,
  "Automotive": Car,
  "Beauty & Personal Care": Scissors,
  "Other": MoreHorizontal
};

function CreateListingModal({ isOpen, onClose, onSuccess, categories }) {
  const [loading, setLoading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    description: "",
    service_area: "",
    phone: "",
    email: "",
    website: "",
    address: "",
    about_owner: ""
  });

  const handleSubmit = async () => {
    if (!formData.name.trim()) { toast.error("Business name is required"); return; }
    if (!formData.category) { toast.error("Please select a category"); return; }
    if (isContentEmpty(formData.description)) { toast.error("Description is required"); return; }
    if (!formData.service_area.trim()) { toast.error("Service area is required"); return; }
    if (!formData.phone && !formData.email && !formData.website) {
      toast.error("At least one contact method is required"); return;
    }

    setLoading(true);
    try {
      const submitData = { 
        ...formData, 
        photos: photos.map(p => p.url), 
        videos: videos.map(v => ({ 
          url: v.url, 
          thumbnail: v.thumbnail || null, 
          name: v.name || null,
          duration: v.duration || null,
          duration_formatted: v.duration_formatted || null
        }))
      };
      await createBusinessListing(submitData);
      toast.success("Business listing created!");
      onSuccess();
      onClose();
      setFormData({ name: "", category: "", description: "", service_area: "", phone: "", email: "", website: "", address: "", about_owner: "" });
      setPhotos([]);
      setVideos([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create listing");
    }
    setLoading(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#E5E0D5] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[var(--liturgical-primary)]" />
            Add Your Business
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 mt-2">
          {/* Notice */}
          <div className="p-3 bg-[#F9F7F2] rounded-lg border border-[#E5E0D5]">
            <p className="text-xs text-[#6B665E] font-['Nunito']">
              By listing your business, you confirm that it is Catholic-owned or that you, 
              as a Catholic member, will personally provide the service.
            </p>
          </div>

          {/* Business Name */}
          <div className="space-y-1.5">
            <label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">Business Name *</label>
            <Input 
              value={formData.name}
              onChange={e => setFormData({...formData, name: e.target.value})}
              placeholder="e.g., Garcia Family Law"
              className="border-[#E5E0D5] font-['Nunito'] text-sm"
              data-testid="business-name-input"
            />
          </div>

          {/* Category */}
          <div className="space-y-1.5">
            <label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">Category *</label>
            <Select value={formData.category} onValueChange={v => setFormData({...formData, category: v})}>
              <SelectTrigger className="border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="business-category-select">
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-60">
                {categories.map(cat => {
                  const Icon = CATEGORY_ICONS[cat] || MoreHorizontal;
                  return (
                    <SelectItem key={cat} value={cat} className="font-['Nunito'] text-sm">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-[#6B665E]" />
                        {cat}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">Description *</label>
            <RichTextEditor
              value={formData.description}
              onChange={(value) => setFormData({...formData, description: value})}
              placeholder="Describe your business and services..."
              minHeight="120px"
            />
          </div>

          {/* Media Upload with Drag & Drop */}
          <MediaUploader
            photos={photos}
            videos={videos}
            onPhotosChange={setPhotos}
            onVideosChange={setVideos}
            maxPhotos={10}
            maxVideos={3}
            enableCropping={true}
          />

          {/* Service Area */}
          <div className="space-y-1.5">
            <label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">Service Area *</label>
            <Input 
              value={formData.service_area}
              onChange={e => setFormData({...formData, service_area: e.target.value})}
              placeholder="e.g., Austin, TX or Nationwide"
              className="border-[#E5E0D5] font-['Nunito'] text-sm"
              data-testid="business-area-input"
            />
          </div>

          {/* Contact Methods */}
          <div className="space-y-3">
            <label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">Contact (at least one required)</label>
            <div className="grid grid-cols-1 gap-3">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#6B665E]" />
                <Input value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} placeholder="Phone number" className="border-[#E5E0D5] font-['Nunito'] text-sm flex-1" data-testid="business-phone-input" />
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#6B665E]" />
                <Input value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} placeholder="Email address" className="border-[#E5E0D5] font-['Nunito'] text-sm flex-1" data-testid="business-email-input" />
              </div>
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-[#6B665E]" />
                <Input value={formData.website} onChange={e => setFormData({...formData, website: e.target.value})} placeholder="Website URL" className="border-[#E5E0D5] font-['Nunito'] text-sm flex-1" data-testid="business-website-input" />
              </div>
            </div>
          </div>

          {/* Optional: Address */}
          <div className="space-y-1.5">
            <label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">Address (optional)</label>
            <Input value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} placeholder="Full business address" className="border-[#E5E0D5] font-['Nunito'] text-sm" />
          </div>

          {/* Optional: About Owner */}
          <div className="space-y-1.5">
            <label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">About the Owner (optional)</label>
            <RichTextEditor
              value={formData.about_owner}
              onChange={(value) => setFormData({...formData, about_owner: value})}
              placeholder="Share a bit about yourself and your faith journey..."
              minHeight="80px"
              minimal={true}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1 border-[#E5E0D5] font-['Nunito']">Cancel</Button>
            <Button onClick={handleSubmit} disabled={loading} className="flex-1 bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']" data-testid="create-business-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Listing"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function DirectoryPage({ currentUser, isPublic = false }) {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState("list");
  const [listings, setListings] = useState([]);
  const [mapListings, setMapListings] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapLoading, setMapLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [sortBy, setSortBy] = useState("rating");
  const [activeTab, setActiveTab] = useState("browse");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [totalListings, setTotalListings] = useState(0);
  
  // Advanced filters
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [minRating, setMinRating] = useState("");
  const [locationQuery, setLocationQuery] = useState("");
  const [hasReviews, setHasReviews] = useState(false);
  const [openNow, setOpenNow] = useState(false);
  const [hasDiscounts, setHasDiscounts] = useState(false);
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [maxDistance, setMaxDistance] = useState("");
  
  // User location and map bounds
  const [userLocation, setUserLocation] = useState(null);
  const [mapBounds, setMapBounds] = useState(null);

  // SEO
  useSEO({
    title: "Catholic Business Directory",
    description: "Find and support Catholic-owned businesses in your community. Browse restaurants, services, professionals, and more from trusted members of your parish.",
    path: "/directory"
  });

  useEffect(() => {
    loadCategories();
    loadListings();
    if (currentUser) {
      loadMyListings();
    }
  }, []);

  useEffect(() => {
    loadListings();
  }, [selectedCategory, sortBy, minRating, hasReviews, openNow, hasDiscounts, featuredOnly, maxDistance, userLocation]);

  // Load map listings when view mode changes or bounds change
  useEffect(() => {
    if (viewMode === "map") {
      loadMapListings();
    }
  }, [viewMode, selectedCategory, openNow, hasDiscounts, minRating, searchQuery, mapBounds]);

  const loadCategories = async () => {
    try {
      const res = await getBusinessCategories();
      setCategories(res.data.categories || []);
    } catch (err) {
      console.error("Failed to load categories:", err);
    }
  };

  const loadListings = async () => {
    setLoading(true);
    try {
      const params = { sort: sortBy };
      if (selectedCategory) params.category = selectedCategory;
      if (searchQuery) params.search = searchQuery;
      if (minRating) params.min_rating = parseFloat(minRating);
      if (hasReviews) params.has_reviews = true;
      if (openNow) params.open_now = true;
      if (hasDiscounts) params.has_discounts = true;
      if (featuredOnly) params.featured_only = true;
      if (userLocation) {
        params.lat = userLocation.lat;
        params.lng = userLocation.lng;
      }
      if (maxDistance) params.max_distance = parseInt(maxDistance);
      
      const res = await getBusinessListings(params);
      setListings(res.data.listings || []);
      setTotalListings(res.data.total || 0);
    } catch (err) {
      toast.error("Failed to load listings");
    }
    setLoading(false);
  };

  const loadMapListings = async () => {
    setMapLoading(true);
    try {
      const params = {};
      if (selectedCategory) params.category = selectedCategory;
      if (searchQuery) params.search = searchQuery;
      if (minRating) params.min_rating = parseFloat(minRating);
      if (openNow) params.open_now = true;
      if (hasDiscounts) params.has_discounts = true;
      if (mapBounds) {
        params.ne_lat = mapBounds.ne_lat;
        params.ne_lng = mapBounds.ne_lng;
        params.sw_lat = mapBounds.sw_lat;
        params.sw_lng = mapBounds.sw_lng;
      }
      
      const res = await getBusinessMapListings(params);
      setMapListings(res.data.listings || []);
    } catch (err) {
      console.error("Failed to load map listings:", err);
    }
    setMapLoading(false);
  };

  const loadMyListings = async () => {
    try {
      const res = await getMyBusinessListings();
      setMyListings(res.data.listings || []);
    } catch (err) {
      console.error("Failed to load my listings:", err);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadListings();
    if (viewMode === "map") {
      loadMapListings();
    }
  };

  const clearFilters = () => {
    setSelectedCategory("");
    setMinRating("");
    setLocationQuery("");
    setUserLocation(null);
    setHasReviews(false);
    setOpenNow(false);
    setHasDiscounts(false);
    setFeaturedOnly(false);
    setMaxDistance("");
    setSearchQuery("");
    setSortBy("rating");
  };

  const handleListingClick = (listing) => {
    navigate(`/directory/${listing.listing_id}`);
  };

  const handleCreateSuccess = () => {
    loadListings();
    loadMyListings();
    if (viewMode === "map") {
      loadMapListings();
    }
  };

  const handleMapBoundsChange = (bounds) => {
    setMapBounds(bounds);
  };

  const isVerifiedMember = currentUser?.parish_id || (currentUser?.parish_ids && currentUser.parish_ids.length > 0);
  const canAddBusiness = isVerifiedMember && !isPublic;

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="directory-page">
      
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        {/* Public Access Banner */}
        {isPublic && (
          <MembershipBanner context="directory" className="mb-6" />
        )}
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-['Crimson_Text'] text-3xl font-bold text-[#2D2A26]">
              Business Directory
            </h1>
            <p className="text-sm text-[#6B665E] font-['Nunito'] mt-1">
              Support fellow parishioners and Catholic-owned businesses
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Job Board Link */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/jobs")}
              className="border-[var(--liturgical-primary)]/30 text-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/5 font-['Nunito'] text-xs"
              data-testid="job-board-link"
            >
              <Briefcase className="w-4 h-4 mr-1.5" />Job Board
            </Button>
            {/* View Toggle */}
            <div className="flex items-center bg-white rounded-lg border border-[#E5E0D5] p-1">
              <Button
                variant={viewMode === "list" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("list")}
                className={`font-['Nunito'] text-xs ${viewMode === "list" ? 'bg-[var(--liturgical-primary)] text-white' : 'text-[#6B665E]'}`}
                data-testid="list-view-btn"
              >
                <List className="w-4 h-4 mr-1" />
                List
              </Button>
              <Button
                variant={viewMode === "map" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("map")}
                className={`font-['Nunito'] text-xs ${viewMode === "map" ? 'bg-[var(--liturgical-primary)] text-white' : 'text-[#6B665E]'}`}
                data-testid="map-view-btn"
              >
                <Map className="w-4 h-4 mr-1" />
                Map
              </Button>
            </div>
            
            {canAddBusiness && (
              <Button 
                onClick={() => setCreateModalOpen(true)}
                className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
                data-testid="add-business-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your Business
              </Button>
            )}
          </div>
        </div>

        {/* Search and Filters */}
        <Card className="bg-white border-[#E5E0D5] mb-6">
          <CardContent className="p-4">
            <DirectoryFilters
              categories={categories}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              sortBy={sortBy}
              setSortBy={setSortBy}
              showAdvancedFilters={showAdvancedFilters}
              setShowAdvancedFilters={setShowAdvancedFilters}
              userLocation={userLocation}
              setUserLocation={setUserLocation}
              maxDistance={maxDistance}
              setMaxDistance={setMaxDistance}
              locationQuery={locationQuery}
              setLocationQuery={setLocationQuery}
              minRating={minRating}
              setMinRating={setMinRating}
              openNow={openNow}
              setOpenNow={setOpenNow}
              hasDiscounts={hasDiscounts}
              setHasDiscounts={setHasDiscounts}
              featuredOnly={featuredOnly}
              setFeaturedOnly={setFeaturedOnly}
              hasReviews={hasReviews}
              setHasReviews={setHasReviews}
              onSearch={handleSearch}
              onClearFilters={clearFilters}
            />
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="bg-[#F0EBE0] border border-[#E5E0D5]">
            <TabsTrigger 
              value="browse" 
              className="font-['Nunito'] text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--liturgical-primary)]"
            >
              Browse All ({totalListings})
            </TabsTrigger>
            {currentUser && (
              <TabsTrigger 
                value="my-listings" 
                className="font-['Nunito'] text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--liturgical-primary)]"
              >
                My Listings ({myListings.length})
              </TabsTrigger>
            )}
          </TabsList>

          {/* Browse Tab */}
          <TabsContent value="browse" className="mt-6">
            {/* Category Quick Filters */}
            <div className="flex flex-wrap gap-2 mb-6">
              <Badge 
                variant={!selectedCategory ? "default" : "outline"}
                className={`cursor-pointer font-['Nunito'] text-xs ${
                  !selectedCategory 
                    ? "bg-[var(--liturgical-primary)] text-white" 
                    : "border-[#E5E0D5] text-[#6B665E] hover:border-[var(--liturgical-primary)]"
                }`}
                onClick={() => setSelectedCategory("")}
              >
                All
              </Badge>
              {categories.slice(0, 8).map(cat => {
                const Icon = CATEGORY_ICONS[cat];
                return (
                  <Badge 
                    key={cat}
                    variant={selectedCategory === cat ? "default" : "outline"}
                    className={`cursor-pointer font-['Nunito'] text-xs ${
                      selectedCategory === cat 
                        ? "bg-[var(--liturgical-primary)] text-white" 
                        : "border-[#E5E0D5] text-[#6B665E] hover:border-[var(--liturgical-primary)]"
                    }`}
                    onClick={() => setSelectedCategory(cat)}
                  >
                    <Icon className="w-3 h-3 mr-1" />
                    {cat}
                  </Badge>
                );
              })}
            </div>

            {/* Content - List or Map View */}
            {viewMode === "list" ? (
              <DirectoryList
                listings={listings}
                loading={loading}
                searchQuery={searchQuery}
                onListingClick={handleListingClick}
              />
            ) : (
              <DirectoryMap
                listings={mapListings}
                userLocation={userLocation}
                loading={mapLoading}
                onBoundsChange={handleMapBoundsChange}
                onViewDetails={(listingId) => navigate(`/directory/${listingId}`)}
              />
            )}
          </TabsContent>

          {/* My Listings Tab */}
          <TabsContent value="my-listings" className="mt-6">
            {myListings.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="w-12 h-12 text-[#E5E0D5] mx-auto mb-3" />
                <p className="text-[#6B665E] font-['Nunito']">You haven't created any listings yet</p>
                {canAddBusiness && (
                  <Button 
                    onClick={() => setCreateModalOpen(true)}
                    className="mt-4 bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Your Business
                  </Button>
                )}
              </div>
            ) : (
              <DirectoryList
                listings={myListings}
                loading={false}
                onListingClick={handleListingClick}
              />
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Listing Modal */}
      <CreateListingModal 
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
        categories={categories}
      />
    </div>
  );
}
